import {
  FC,
  PointerEvent as ReactPointerEvent,
  WheelEvent as ReactWheelEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Boxes,
  Download,
  Keyboard,
  Link2,
  Map as MapIcon,
  Maximize2,
  Minus,
  Palette,
  Plus,
  X,
} from 'lucide-react';
import * as Sentry from '@sentry/react';
import {
  Simulation,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
} from 'd3-force';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useNotification } from '@/components/notifications';
import { cn } from '@/lib/utils';
import {
  copyGraphPng,
  exportGraphPng,
  exportGraphSvg,
} from '@/components/context-graph-viewer/export-image';
import {
  GRAPH_PALETTES,
  PALETTE_STORAGE_KEY,
  getStoredPalette,
} from '@/components/context-graph-viewer/palettes';
import { DetailCard } from '@/components/context-graph-viewer/detail-card';
import { GraphLink } from '@/components/context-graph-viewer/graph-link';
import { GraphNode } from '@/components/context-graph-viewer/graph-node';
import { GraphSearchInput } from '@/components/context-graph-viewer/graph-search-input';
import { TimelineScrubber } from '@/components/context-graph-viewer/timeline-scrubber';
import {
  detectCommunities,
  hullPath,
} from '@/components/context-graph-viewer/helpers/communities';
import {
  GraphSelection,
  SimLink,
  SimNode,
  linkEndpointIds,
  linkEndpoints,
  linkMidpoint,
  linkPath,
  nodeRadius,
  parseTime,
  truncateLabel,
} from '@/components/context-graph-viewer/helpers/graph-types';
import {
  VIEW_STATE_PARAM,
  ViewerViewState,
  encodeViewState,
} from '@/components/context-graph-viewer/helpers/view-state';
import { useContextGraphNodeSearch } from '@/hooks/queries/use-context-graph-node-search';
import {
  ContextGraphVisualizationEdge,
  ContextGraphVisualizationNode,
} from '@/types/context-graph-types';

interface ForceGraphProps {
  nodes: ContextGraphVisualizationNode[];
  edges: ContextGraphVisualizationEdge[];
  graphId: string;
  graphName: string;
  truncated?: boolean;
  initialViewState?: ViewerViewState | null;
  canEdit?: boolean;
  onGraphMutated?: (selection: GraphSelection) => void;
  className?: string;
}

interface Cluster {
  id: number;
  nodes: SimNode[];
  color: string;
  label: string;
}

const MIN_ZOOM = 0.15;
const MAX_ZOOM = 8;
const LABEL_ZOOM_THRESHOLD = 1.25;
const ALWAYS_LABELED_COUNT = 14;
const FOCUS_TWO_HOP_LIMIT = 60;
const MAX_PARALLEL_ARCS = 5;
const MINIMAP_THRESHOLD = 60;
const MINIMAP_W = 160;
const MINIMAP_H = 110;
const CLUSTERS_STORAGE_KEY = 'context-graph-viewer-clusters';

const SHORTCUTS: Array<[string, string]> = [
  ['/', 'Focus search'],
  ['↑ ↓ ← →', 'Walk neighbors'],
  ['Enter', 'Open details'],
  ['Double-click', 'Focus neighborhood'],
  ['Shift+drag', 'Select area'],
  ['Esc', 'Close / back'],
];

interface Marquee {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export const ForceGraph: FC<ForceGraphProps> = ({
  nodes,
  edges,
  graphId,
  graphName,
  truncated = false,
  initialViewState,
  canEdit = false,
  onGraphMutated,
  className,
}) => {
  const notification = useNotification();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const sceneRef = useRef<SVGGElement>(null);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const shareStateRef = useRef<() => ViewerViewState>(() => ({}));
  const linkElementsRef = useRef(new Map<string, SVGGElement>());
  const nodeElementsRef = useRef(new Map<string, SVGGElement>());
  const hullElementsRef = useRef(new Map<number, SVGGElement>());
  const minimapSvgRef = useRef<SVGSVGElement>(null);
  const minimapDotsRef = useRef(new Map<string, SVGCircleElement>());
  const minimapRectRef = useRef<SVGRectElement>(null);
  const minimapTransformRef = useRef<{
    scale: number;
    ox: number;
    oy: number;
  } | null>(null);
  const minimapDraggingRef = useRef(false);
  const simulationRef = useRef<Simulation<SimNode, SimLink> | null>(null);
  const viewRef = useRef({ x: 0, y: 0, k: 1 });
  const initialViewConsumedRef = useRef(false);
  const restoredSelectionRef = useRef(false);
  const shareTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const updatePositionsRef = useRef<() => void>(() => {});
  const focusEdgeLabelRef = useRef<SVGTextElement | null>(null);
  const focusEdgeRef = useRef<SimLink | null>(null);
  const dragRef = useRef<{
    node: SimNode | null;
    panning: boolean;
    marquee: boolean;
    lastX: number;
    lastY: number;
    moved: boolean;
  }>({
    node: null,
    panning: false,
    marquee: false,
    lastX: 0,
    lastY: 0,
    moved: false,
  });
  const viewAnimRef = useRef<number | null>(null);
  const aoiHintTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const marqueeRef = useRef<Marquee | null>(null);
  const simNodeCacheRef = useRef(new Map<string, SimNode>());
  const layoutReadyRef = useRef(false);
  const confirmOpenRef = useRef(false);
  // Latest values for the register-once window keydown handler; assigned
  // every render so the handler never closes over stale state.
  const keyStateRef = useRef<{
    selection: GraphSelection | null;
    aoiIds: Set<string> | null;
    focusRootId: string | null;
    hoveredNodeId: string | null;
    nodesById: Map<string, SimNode>;
    moveCursor: (key: string) => void;
    exitAoi: () => void;
    exitFocus: () => void;
  }>(null!);

  const [size, setSize] = useState({ width: 0, height: 0 });
  const [layoutReady, setLayoutReady] = useState(false);
  layoutReadyRef.current = layoutReady;
  const [showAllLabels, setShowAllLabels] = useState(false);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);
  const [selection, setSelection] = useState<GraphSelection | null>(null);
  const [searchQuery, setSearchQuery] = useState(initialViewState?.q ?? '');
  const [paletteId, setPaletteId] = useState(() =>
    initialViewState?.palette &&
    GRAPH_PALETTES.some((palette) => palette.id === initialViewState.palette)
      ? initialViewState.palette
      : getStoredPalette().id,
  );
  const [showClusters, setShowClusters] = useState(
    () =>
      initialViewState?.clusters ??
      localStorage.getItem(CLUSTERS_STORAGE_KEY) === '1',
  );
  const [timeT, setTimeT] = useState<number | null>(
    initialViewState?.t ?? null,
  );
  const [playing, setPlaying] = useState(false);
  const [focusRootId, setFocusRootId] = useState<string | null>(
    initialViewState?.focus ?? null,
  );
  const [minimapOpen, setMinimapOpen] = useState(true);
  const [aoiIds, setAoiIds] = useState<Set<string> | null>(null);
  const [aoiEmptyHint, setAoiEmptyHint] = useState(false);

  // The marquee rectangle is drawn with direct style writes (like panning via
  // viewRef/applyView) so a shift-drag never re-renders the scene.
  const marqueeDivRef = useRef<HTMLDivElement>(null);
  const drawMarquee = useCallback((box: Marquee | null) => {
    const element = marqueeDivRef.current;
    if (!element) return;
    if (!box) {
      element.style.display = 'none';
      return;
    }
    element.style.display = 'block';
    element.style.left = `${Math.min(box.x0, box.x1)}px`;
    element.style.top = `${Math.min(box.y0, box.y1)}px`;
    element.style.width = `${Math.abs(box.x1 - box.x0)}px`;
    element.style.height = `${Math.abs(box.y1 - box.y0)}px`;
  }, []);

  // The visualization payload is degree-capped, so on truncated graphs the
  // search also queries the server across the FULL graph and splices matches
  // (plus their neighborhood) into the rendered view.
  const { data: serverSearch } = useContextGraphNodeSearch(
    graphId,
    searchQuery,
    truncated,
  );

  const { mergedNodes, mergedEdges, serverMatchedIds } = useMemo(() => {
    const trimmedQuery = searchQuery.trim();
    // serverSearch.query echoes the query the payload answers. Requiring it
    // to match the live input drops stale keepPreviousData payloads — both
    // mid-flight after the query changed and lingering ones after the input
    // fell below the fetch threshold — instead of splicing the previous
    // query's matches in as authoritative.
    if (
      !truncated ||
      !trimmedQuery ||
      !serverSearch ||
      serverSearch.query !== trimmedQuery
    ) {
      return {
        mergedNodes: nodes,
        mergedEdges: edges,
        serverMatchedIds: null as Set<string> | null,
      };
    }
    const knownNodeIds = new Set(nodes.map((node) => node.id));
    const extraNodes = serverSearch.nodes.filter(
      (node) => !knownNodeIds.has(node.id),
    );
    extraNodes.forEach((node) => knownNodeIds.add(node.id));
    const knownEdgeIds = new Set(edges.map((edge) => edge.id));
    const extraEdges = serverSearch.edges.filter(
      (edge) =>
        !knownEdgeIds.has(edge.id) &&
        knownNodeIds.has(edge.source) &&
        knownNodeIds.has(edge.target),
    );
    return {
      mergedNodes: extraNodes.length ? [...nodes, ...extraNodes] : nodes,
      mergedEdges: extraEdges.length ? [...edges, ...extraEdges] : edges,
      serverMatchedIds: new Set(serverSearch.matched_ids),
    };
  }, [nodes, edges, serverSearch, searchQuery, truncated]);

  const { simNodes, simLinks, timeRange } = useMemo(() => {
    // Reuse SimNode objects across data changes (e.g. a manual removal) so
    // simulation positions survive and the layout doesn't reset.
    const cache = simNodeCacheRef.current;
    const builtNodes: SimNode[] = mergedNodes.map((node) => {
      const fields = {
        ...node,
        radius: nodeRadius(node.degree),
        startTime: parseTime(node.created_at),
      };
      const existing = cache.get(node.id);
      if (existing) {
        return Object.assign(existing, fields);
      }
      cache.set(node.id, fields);
      return fields;
    });
    const liveIds = new Set(builtNodes.map((n) => n.id));
    [...cache.keys()].forEach((id) => {
      if (!liveIds.has(id)) cache.delete(id);
    });
    const nodeIds = new Set(builtNodes.map((n) => n.id));
    const inViewEdges = mergedEdges.filter(
      (edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target),
    );
    const pairTotals = new Map<string, number>();
    inViewEdges.forEach((edge) => {
      const pairKey = [edge.source, edge.target].sort().join('::');
      pairTotals.set(pairKey, (pairTotals.get(pairKey) ?? 0) + 1);
    });
    const pairCounts = new Map<string, number>();
    const builtLinks: SimLink[] = inViewEdges.map((edge) => {
      const pairKey = [edge.source, edge.target].sort().join('::');
      const seen = pairCounts.get(pairKey) ?? 0;
      pairCounts.set(pairKey, seen + 1);
      // 0, 1, -1, 2, -2… so parallel facts fan out as gentle arcs.
      const curve =
        seen === 0 ? 0 : Math.ceil(seen / 2) * (seen % 2 === 1 ? 1 : -1);
      return {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: edge.label,
        fact: edge.fact,
        validAt: edge.valid_at,
        curve,
        hidden: seen >= MAX_PARALLEL_ARCS,
        pairCount: pairTotals.get(pairKey),
        startTime: parseTime(edge.valid_at) ?? parseTime(edge.created_at),
        endTime: parseTime(edge.invalid_at) ?? Infinity,
      };
    });

    // A node enters the timeline with its first fact; created_at is the
    // fallback for isolated nodes.
    const firstFact = new Map<string, number>();
    builtLinks.forEach((link) => {
      if (link.startTime == null) return;
      [String(link.source), String(link.target)].forEach((id) => {
        const current = firstFact.get(id);
        if (current === undefined || link.startTime! < current) {
          firstFact.set(id, link.startTime!);
        }
      });
    });
    builtNodes.forEach((node) => {
      node.startTime = firstFact.get(node.id) ?? node.startTime;
    });

    const stamps: number[] = [];
    builtNodes.forEach((n) => {
      if (n.startTime != null) stamps.push(n.startTime);
    });
    builtLinks.forEach((l) => {
      if (l.startTime != null) stamps.push(l.startTime);
      if (Number.isFinite(l.endTime)) stamps.push(l.endTime);
    });
    const min = Math.min(...stamps);
    const max = Math.max(...stamps);
    const range = stamps.length >= 2 && max - min > 1000 ? { min, max } : null;
    return { simNodes: builtNodes, simLinks: builtLinks, timeRange: range };
  }, [mergedNodes, mergedEdges]);

  const fullAdjacency = useMemo(() => {
    const map = new Map<string, Set<string>>();
    simNodes.forEach((node) => map.set(node.id, new Set()));
    simLinks.forEach((link) => {
      const [sourceId, targetId] = linkEndpointIds(link);
      map.get(sourceId)?.add(targetId);
      map.get(targetId)?.add(sourceId);
    });
    return map;
  }, [simNodes, simLinks]);

  const focusIds = useMemo(() => {
    if (!focusRootId || !fullAdjacency.has(focusRootId)) return null;
    const oneHop = new Set([
      focusRootId,
      ...(fullAdjacency.get(focusRootId) ?? []),
    ]);
    const twoHop = new Set(oneHop);
    oneHop.forEach((id) =>
      fullAdjacency.get(id)?.forEach((neighbor) => twoHop.add(neighbor)),
    );
    return twoHop.size <= FOCUS_TWO_HOP_LIMIT ? twoHop : oneHop;
  }, [focusRootId, fullAdjacency]);

  const displayNodes = useMemo(
    () =>
      focusIds ? simNodes.filter((node) => focusIds.has(node.id)) : simNodes,
    [simNodes, focusIds],
  );

  const displayLinks = useMemo(() => {
    if (!focusIds) return simLinks;
    return simLinks.filter((link) => {
      const [sourceId, targetId] = linkEndpointIds(link);
      return focusIds.has(sourceId) && focusIds.has(targetId);
    });
  }, [simLinks, focusIds]);

  // Overflow parallel facts stay in displayLinks (search/adjacency) but are
  // excluded from the simulation and the SVG.
  const renderedLinks = useMemo(
    () => displayLinks.filter((link) => !link.hidden),
    [displayLinks],
  );

  // Per-node rendered-link counts, used to weaken springs on hubs (mirrors
  // d3's default link strength) so dense graphs don't collapse into a blob.
  const renderedLinkCounts = useMemo(() => {
    const counts = new Map<string, number>();
    renderedLinks.forEach((link) => {
      const [sourceId, targetId] = linkEndpointIds(link);
      counts.set(sourceId, (counts.get(sourceId) ?? 0) + 1);
      counts.set(targetId, (counts.get(targetId) ?? 0) + 1);
    });
    return counts;
  }, [renderedLinks]);

  const nodesById = useMemo(() => {
    const map = new Map<string, SimNode>();
    displayNodes.forEach((node) => map.set(node.id, node));
    return map;
  }, [displayNodes]);

  const adjacency = useMemo(() => {
    const map = new Map<string, Set<string>>();
    displayNodes.forEach((node) => map.set(node.id, new Set()));
    displayLinks.forEach((link) => {
      const [sourceId, targetId] = linkEndpointIds(link);
      map.get(sourceId)?.add(targetId);
      map.get(targetId)?.add(sourceId);
    });
    return map;
  }, [displayNodes, displayLinks]);

  const activePalette = useMemo(
    () =>
      GRAPH_PALETTES.find((palette) => palette.id === paletteId) ??
      GRAPH_PALETTES[0],
    [paletteId],
  );

  const typeColors = useMemo(() => {
    const types = [...new Set(simNodes.map((node) => node.type))].sort();
    return new Map(
      types.map((type, index) => [
        type,
        activePalette.colors[index % activePalette.colors.length],
      ]),
    );
  }, [simNodes, activePalette]);

  const typeCounts = useMemo(() => {
    const counts = new Map<string, number>();
    displayNodes.forEach((node) =>
      counts.set(node.type, (counts.get(node.type) ?? 0) + 1),
    );
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [displayNodes]);

  const alwaysLabeledIds = useMemo(() => {
    const byDegree = [...displayNodes].sort((a, b) => b.degree - a.degree);
    return new Set(byDegree.slice(0, ALWAYS_LABELED_COUNT).map((n) => n.id));
  }, [displayNodes]);

  // A search hit on an edge's fact also lights up both of its endpoints, so
  // searching for content (not just entity names) still lands on nodes.
  const searchMatch = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return null;
    const nodeIds = new Set<string>();
    const edgeIds = new Set<string>();
    // Tokenized so out-of-order partial words still match ("tir sree").
    const tokens = query.split(/\s+/).filter(Boolean);
    displayNodes.forEach((node) => {
      if (serverMatchedIds?.has(node.id)) {
        nodeIds.add(node.id);
        return;
      }
      const haystack =
        `${node.label} ${node.type} ${node.summary}`.toLowerCase();
      if (tokens.every((token) => haystack.includes(token))) {
        nodeIds.add(node.id);
      }
    });
    displayLinks.forEach((link) => {
      const haystack = `${link.fact} ${link.label}`.toLowerCase();
      if (tokens.every((token) => haystack.includes(token))) {
        edgeIds.add(link.id);
        const [sourceId, targetId] = linkEndpointIds(link);
        nodeIds.add(sourceId);
        nodeIds.add(targetId);
      }
    });
    return { nodeIds, edgeIds };
  }, [searchQuery, displayNodes, displayLinks, serverMatchedIds]);

  const clusters = useMemo<Cluster[] | null>(() => {
    if (!showClusters) return null;
    const assignment = detectCommunities(
      displayNodes.map((node) => node.id),
      adjacency,
    );
    const groups = new Map<number, SimNode[]>();
    displayNodes.forEach((node) => {
      const community = assignment.get(node.id);
      if (community === undefined) return;
      const members = groups.get(community) ?? [];
      members.push(node);
      groups.set(community, members);
    });
    const multiType = new Set(simNodes.map((node) => node.type)).size > 1;
    return [...groups.entries()]
      .filter(([, members]) => members.length >= 3)
      .map(([id, members]) => {
        let label: string;
        if (multiType) {
          const typeCount = new Map<string, number>();
          members.forEach((member) =>
            typeCount.set(member.type, (typeCount.get(member.type) ?? 0) + 1),
          );
          label = [...typeCount.entries()].sort((a, b) => b[1] - a[1])[0][0];
        } else {
          label = [...members].sort((a, b) => b.degree - a.degree)[0].label;
        }
        return {
          id,
          nodes: members,
          color: activePalette.colors[id % activePalette.colors.length],
          label: truncateLabel(label, 30),
        };
      });
  }, [showClusters, displayNodes, adjacency, activePalette, simNodes]);

  // timeT === null means the scrubber is parked at "now": no temporal
  // filtering at all, so invalidated facts still show in the default view.
  const nodeInTime = useCallback(
    (node: SimNode): boolean =>
      timeT == null || node.startTime == null || node.startTime <= timeT,
    [timeT],
  );
  const linkInTime = useCallback(
    (link: SimLink): boolean =>
      timeT == null ||
      ((link.startTime == null || link.startTime <= timeT) &&
        timeT < link.endTime),
    [timeT],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const scheduleShareSync = useCallback(() => {
    clearTimeout(shareTimerRef.current);
    shareTimerRef.current = setTimeout(() => {
      const url = new URL(window.location.href);
      url.searchParams.set(
        VIEW_STATE_PARAM,
        encodeViewState(shareStateRef.current()),
      );
      window.history.replaceState(null, '', url.toString());
    }, 400);
  }, []);

  const updateMinimapViewport = useCallback(() => {
    const transform = minimapTransformRef.current;
    const rect = minimapRectRef.current;
    if (!transform || !rect || !size.width) return;
    const { x, y, k } = viewRef.current;
    rect.setAttribute(
      'x',
      String(((0 - x) / k) * transform.scale + transform.ox),
    );
    rect.setAttribute(
      'y',
      String(((0 - y) / k) * transform.scale + transform.oy),
    );
    rect.setAttribute('width', String((size.width / k) * transform.scale));
    rect.setAttribute('height', String((size.height / k) * transform.scale));
  }, [size]);

  const applyView = useCallback(() => {
    const { x, y, k } = viewRef.current;
    sceneRef.current?.setAttribute(
      'transform',
      `translate(${x}, ${y}) scale(${k})`,
    );
    setShowAllLabels(k >= LABEL_ZOOM_THRESHOLD);
    updateMinimapViewport();
    scheduleShareSync();
  }, [updateMinimapViewport, scheduleShareSync]);

  const updatePositions = useCallback(() => {
    renderedLinks.forEach((link) => {
      const group = linkElementsRef.current.get(link.id);
      if (!group) return;
      const path = linkPath(link);
      for (const child of group.children) {
        child.setAttribute('d', path);
      }
    });
    displayNodes.forEach((node) => {
      nodeElementsRef.current
        .get(node.id)
        ?.setAttribute(
          'transform',
          `translate(${node.x ?? 0}, ${node.y ?? 0})`,
        );
    });

    // The focused-edge caption is positioned at render time; keep it glued to
    // the arc while the simulation reheats or an endpoint is dragged.
    const focusLabel = focusEdgeLabelRef.current;
    const focusedEdge = focusEdgeRef.current;
    if (focusLabel && focusedEdge) {
      const mid = linkMidpoint(focusedEdge);
      focusLabel.setAttribute('x', String(mid.x));
      focusLabel.setAttribute('y', String(mid.y - 6));
    }

    if (clusters) {
      clusters.forEach((cluster) => {
        const group = hullElementsRef.current.get(cluster.id);
        if (!group) return;
        const members = cluster.nodes.filter(nodeInTime);
        const path = group.querySelector('path');
        const text = group.querySelector('text');
        if (members.length < 2) {
          group.setAttribute('opacity', '0');
          return;
        }
        group.setAttribute('opacity', '1');
        const points = members.map(
          (member) => [member.x ?? 0, member.y ?? 0] as [number, number],
        );
        path?.setAttribute('d', hullPath(points));
        if (text) {
          const minY = Math.min(...points.map((p) => p[1]));
          const cx = points.reduce((sum, p) => sum + p[0], 0) / points.length;
          text.setAttribute('x', String(cx));
          text.setAttribute('y', String(minY - 34));
        }
      });
    }

    if (minimapOpen && displayNodes.length > MINIMAP_THRESHOLD) {
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      displayNodes.forEach((node) => {
        minX = Math.min(minX, node.x ?? 0);
        minY = Math.min(minY, node.y ?? 0);
        maxX = Math.max(maxX, node.x ?? 0);
        maxY = Math.max(maxY, node.y ?? 0);
      });
      const scale = Math.min(
        (MINIMAP_W - 14) / Math.max(maxX - minX, 1),
        (MINIMAP_H - 14) / Math.max(maxY - minY, 1),
      );
      const transform = {
        scale,
        ox: MINIMAP_W / 2 - ((minX + maxX) / 2) * scale,
        oy: MINIMAP_H / 2 - ((minY + maxY) / 2) * scale,
      };
      minimapTransformRef.current = transform;
      displayNodes.forEach((node) => {
        const dot = minimapDotsRef.current.get(node.id);
        if (!dot) return;
        dot.setAttribute(
          'cx',
          String((node.x ?? 0) * transform.scale + transform.ox),
        );
        dot.setAttribute(
          'cy',
          String((node.y ?? 0) * transform.scale + transform.oy),
        );
        dot.setAttribute('opacity', nodeInTime(node) ? '0.85' : '0');
      });
      updateMinimapViewport();
    }
  }, [
    renderedLinks,
    displayNodes,
    clusters,
    nodeInTime,
    minimapOpen,
    updateMinimapViewport,
  ]);

  updatePositionsRef.current = updatePositions;

  useEffect(() => {
    updatePositionsRef.current();
  }, [timeT, minimapOpen, clusters, displayNodes, size, layoutReady]);

  const computeFit = useCallback(
    (targets: SimNode[]): { x: number; y: number; k: number } | null => {
      const { width, height } = size;
      if (!width || !height || targets.length === 0) return null;
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      targets.forEach((node) => {
        minX = Math.min(minX, (node.x ?? 0) - node.radius);
        minY = Math.min(minY, (node.y ?? 0) - node.radius);
        maxX = Math.max(maxX, (node.x ?? 0) + node.radius);
        maxY = Math.max(maxY, (node.y ?? 0) + node.radius);
      });
      const boundsWidth = Math.max(maxX - minX, 1);
      const boundsHeight = Math.max(maxY - minY, 1);
      const k = Math.min(
        Math.max(
          Math.min(
            (width / boundsWidth) * 0.82,
            (height / boundsHeight) * 0.82,
          ),
          MIN_ZOOM,
        ),
        2.2,
      );
      return {
        k,
        x: width / 2 - ((minX + maxX) / 2) * k,
        y: height / 2 - ((minY + maxY) / 2) * k,
      };
    },
    [size],
  );

  const fitToView = useCallback(() => {
    const fit = computeFit(displayNodes);
    if (!fit) return;
    viewRef.current = fit;
    applyView();
  }, [applyView, computeFit, displayNodes]);

  const animateViewTo = useCallback(
    (target: { x: number; y: number; k: number }) => {
      if (viewAnimRef.current) cancelAnimationFrame(viewAnimRef.current);
      const from = { ...viewRef.current };
      const start = performance.now();
      const duration = 350;
      const ease = (t: number) =>
        t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const frame = (now: number) => {
        const t = Math.min((now - start) / duration, 1);
        const e = ease(t);
        viewRef.current = {
          x: from.x + (target.x - from.x) * e,
          y: from.y + (target.y - from.y) * e,
          k: from.k + (target.k - from.k) * e,
        };
        applyView();
        viewAnimRef.current = t < 1 ? requestAnimationFrame(frame) : null;
      };
      viewAnimRef.current = requestAnimationFrame(frame);
    },
    [applyView],
  );

  useEffect(
    () => () => {
      if (viewAnimRef.current) cancelAnimationFrame(viewAnimRef.current);
      clearTimeout(aoiHintTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!size.width || !size.height || displayNodes.length === 0) return;

    const simulation = forceSimulation<SimNode, SimLink>(displayNodes)
      .force(
        'link',
        forceLink<SimNode, SimLink>(renderedLinks)
          .id((node) => node.id)
          .distance(
            (link) =>
              64 +
              (link.source as SimNode).radius +
              (link.target as SimNode).radius,
          )
          .strength((link) => {
            const [sourceId, targetId] = linkEndpointIds(link);
            const minCount = Math.min(
              renderedLinkCounts.get(sourceId) ?? 1,
              renderedLinkCounts.get(targetId) ?? 1,
            );
            return Math.min(0.35, 1 / minCount);
          }),
      )
      .force('charge', forceManyBody().strength(-220))
      .force(
        'collide',
        forceCollide<SimNode>().radius((node) => node.radius + 7),
      )
      .force('x', forceX(0).strength(0.045))
      .force('y', forceY(0).strength(0.045))
      .stop();

    simulation.on('tick', () => updatePositionsRef.current());

    if (!layoutReadyRef.current) {
      // Fresh layout (first open or focus change): settle off-screen, fit the
      // viewport, then release at low alpha so the graph eases into place.
      const settleTicks = Math.min(300, 60 + displayNodes.length * 2);
      simulation.tick(settleTicks);
      updatePositionsRef.current();
      if (initialViewState?.view && !initialViewConsumedRef.current) {
        initialViewConsumedRef.current = true;
        viewRef.current = { ...initialViewState.view };
        applyView();
      } else {
        fitToView();
      }
      setLayoutReady(true);
      simulation.alpha(0.06).restart();
    } else {
      // Data changed in place (e.g. a node/fact was removed): keep the view
      // and existing positions, just reheat gently so the layout adapts.
      updatePositionsRef.current();
      simulation.alpha(0.25).restart();
    }
    simulationRef.current = simulation;

    return () => {
      simulation.stop();
      simulationRef.current = null;
    };
    // fitToView changes with `size`; re-running the simulation on resize would
    // discard user layout, so only data changes rebuild it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayNodes, renderedLinks, Boolean(size.width && size.height)]);

  useEffect(() => {
    if (restoredSelectionRef.current) return;
    const selId = initialViewState?.sel;
    if (!selId) {
      restoredSelectionRef.current = true;
      return;
    }
    // Only mark the restore consumed once the node is actually found — the
    // shared node can be absent on the first pass (e.g. hidden by an active
    // neighborhood focus) and should still restore on a later data update.
    const node = nodesById.get(selId);
    if (node) {
      restoredSelectionRef.current = true;
      setSelection({ kind: 'node', node });
    }
  }, [nodesById, initialViewState]);

  useEffect(() => {
    if (!playing || !timeRange) return;
    const step = (timeRange.max - timeRange.min) / 240;
    const interval = setInterval(() => {
      setTimeT((prev) => {
        const current = prev ?? timeRange.min;
        const next = current + step;
        if (next >= timeRange.max) {
          setPlaying(false);
          // End of playback means "now" — mirror the scrubber, which maps the
          // end position to null (no temporal filter) so invalidated facts
          // don't vanish after play finishes.
          return null;
        }
        return next;
      });
    }, 50);
    return () => clearInterval(interval);
  }, [playing, timeRange]);

  const handleTogglePlay = useCallback(() => {
    if (!timeRange) return;
    setPlaying((wasPlaying) => {
      if (wasPlaying) return false;
      setTimeT((prev) =>
        prev == null || prev >= timeRange.max ? timeRange.min : prev,
      );
      return true;
    });
  }, [timeRange]);

  const handlePaletteChange = useCallback((id: string) => {
    setPaletteId(id);
    localStorage.setItem(PALETTE_STORAGE_KEY, id);
  }, []);

  const handleToggleClusters = useCallback(() => {
    setShowClusters((prev) => {
      localStorage.setItem(CLUSTERS_STORAGE_KEY, prev ? '0' : '1');
      return !prev;
    });
  }, []);

  const enterFocus = useCallback((nodeId: string) => {
    setFocusRootId(nodeId);
    setSelection(null);
    setHoveredNodeId(null);
    setAoiIds(null);
    setLayoutReady(false);
  }, []);

  const exitFocus = useCallback(() => {
    setFocusRootId(null);
    setHoveredNodeId(null);
    setAoiIds(null);
    setLayoutReady(false);
  }, []);

  const exitAoi = useCallback(() => {
    setAoiIds(null);
    const fit = computeFit(displayNodes);
    if (fit) animateViewTo(fit);
  }, [computeFit, displayNodes, animateViewTo]);

  const toGraphPoint = useCallback((clientX: number, clientY: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    const { x, y, k } = viewRef.current;
    return {
      x: (clientX - (rect?.left ?? 0) - x) / k,
      y: (clientY - (rect?.top ?? 0) - y) / k,
    };
  }, []);

  const handleWheel = useCallback(
    (event: ReactWheelEvent<SVGSVGElement>) => {
      const rect = svgRef.current?.getBoundingClientRect();
      const cx = event.clientX - (rect?.left ?? 0);
      const cy = event.clientY - (rect?.top ?? 0);
      const { x, y, k } = viewRef.current;
      const nextK = Math.min(
        Math.max(k * Math.exp(-event.deltaY * 0.0022), MIN_ZOOM),
        MAX_ZOOM,
      );
      viewRef.current = {
        k: nextK,
        x: cx - ((cx - x) / k) * nextK,
        y: cy - ((cy - y) / k) * nextK,
      };
      applyView();
    },
    [applyView],
  );

  const zoomBy = useCallback(
    (factor: number) => {
      const { width, height } = size;
      const { x, y, k } = viewRef.current;
      const nextK = Math.min(Math.max(k * factor, MIN_ZOOM), MAX_ZOOM);
      viewRef.current = {
        k: nextK,
        x: width / 2 - ((width / 2 - x) / k) * nextK,
        y: height / 2 - ((height / 2 - y) / k) * nextK,
      };
      applyView();
    },
    [applyView, size],
  );

  const handleNodePointerDown = useCallback(
    (event: ReactPointerEvent, node: SimNode) => {
      event.stopPropagation();
      svgRef.current?.setPointerCapture(event.pointerId);
      dragRef.current = {
        node,
        panning: false,
        marquee: false,
        lastX: event.clientX,
        lastY: event.clientY,
        moved: false,
      };
      simulationRef.current?.alphaTarget(0.18).restart();
      setSelection({ kind: 'node', node });
    },
    [],
  );

  const selectLink = useCallback((link: SimLink) => {
    setSelection({ kind: 'edge', link });
  }, []);

  const registerNodeElement = useCallback(
    (id: string, element: SVGGElement | null) => {
      if (element) {
        nodeElementsRef.current.set(id, element);
      } else {
        nodeElementsRef.current.delete(id);
      }
    },
    [],
  );

  const registerLinkElement = useCallback(
    (id: string, element: SVGGElement | null) => {
      if (element) {
        linkElementsRef.current.set(id, element);
      } else {
        linkElementsRef.current.delete(id);
      }
    },
    [],
  );

  const toLocalPoint = useCallback((clientX: number, clientY: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    return {
      x: clientX - (rect?.left ?? 0),
      y: clientY - (rect?.top ?? 0),
    };
  }, []);

  const handleBackgroundPointerDown = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      svgRef.current?.setPointerCapture(event.pointerId);
      const isMarquee = event.shiftKey;
      dragRef.current = {
        node: null,
        panning: !isMarquee,
        marquee: isMarquee,
        lastX: event.clientX,
        lastY: event.clientY,
        moved: false,
      };
      if (isMarquee) {
        const point = toLocalPoint(event.clientX, event.clientY);
        const box = { x0: point.x, y0: point.y, x1: point.x, y1: point.y };
        marqueeRef.current = box;
        drawMarquee(box);
      }
    },
    [toLocalPoint, drawMarquee],
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      const drag = dragRef.current;
      if (drag.node) {
        const point = toGraphPoint(event.clientX, event.clientY);
        drag.node.fx = point.x;
        drag.node.fy = point.y;
        drag.moved = true;
      } else if (drag.marquee) {
        const point = toLocalPoint(event.clientX, event.clientY);
        const prev = marqueeRef.current;
        if (prev) {
          const box = { ...prev, x1: point.x, y1: point.y };
          marqueeRef.current = box;
          drawMarquee(box);
        }
        drag.moved = true;
      } else if (drag.panning) {
        viewRef.current.x += event.clientX - drag.lastX;
        viewRef.current.y += event.clientY - drag.lastY;
        drag.lastX = event.clientX;
        drag.lastY = event.clientY;
        drag.moved = true;
        applyView();
      }
    },
    [applyView, toGraphPoint, toLocalPoint, drawMarquee],
  );

  const finishMarquee = useCallback(
    (box: Marquee) => {
      const { x, y, k } = viewRef.current;
      const gx0 = (Math.min(box.x0, box.x1) - x) / k;
      const gx1 = (Math.max(box.x0, box.x1) - x) / k;
      const gy0 = (Math.min(box.y0, box.y1) - y) / k;
      const gy1 = (Math.max(box.y0, box.y1) - y) / k;
      const members = displayNodes.filter(
        (node) =>
          nodeInTime(node) &&
          (node.x ?? 0) >= gx0 &&
          (node.x ?? 0) <= gx1 &&
          (node.y ?? 0) >= gy0 &&
          (node.y ?? 0) <= gy1,
      );
      if (members.length === 0) {
        setAoiEmptyHint(true);
        clearTimeout(aoiHintTimerRef.current);
        aoiHintTimerRef.current = setTimeout(
          () => setAoiEmptyHint(false),
          1600,
        );
        return;
      }
      setAoiIds(new Set(members.map((node) => node.id)));
      setSelection(null);
      const fit = computeFit(members);
      if (fit) animateViewTo(fit);
    },
    [displayNodes, nodeInTime, computeFit, animateViewTo],
  );

  const handlePointerUp = useCallback(() => {
    const drag = dragRef.current;
    if (drag.node) {
      drag.node.fx = null;
      drag.node.fy = null;
      simulationRef.current?.alphaTarget(0);
    }
    if (drag.marquee) {
      const box = marqueeRef.current;
      if (box && drag.moved) finishMarquee(box);
      marqueeRef.current = null;
      drawMarquee(null);
    }
    if (drag.panning && !drag.moved) {
      setSelection(null);
    }
    dragRef.current = {
      node: null,
      panning: false,
      marquee: false,
      lastX: 0,
      lastY: 0,
      moved: false,
    };
  }, [finishMarquee, drawMarquee]);

  const minimapNavigate = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      const transform = minimapTransformRef.current;
      const rect = minimapSvgRef.current?.getBoundingClientRect();
      if (!transform || !rect) return;
      const gx = (event.clientX - rect.left - transform.ox) / transform.scale;
      const gy = (event.clientY - rect.top - transform.oy) / transform.scale;
      const { k } = viewRef.current;
      viewRef.current = {
        k,
        x: size.width / 2 - gx * k,
        y: size.height / 2 - gy * k,
      };
      applyView();
    },
    [applyView, size],
  );

  const focusNodeId =
    hoveredNodeId ?? (selection?.kind === 'node' ? selection.node.id : null);
  const focusEdgeId =
    hoveredEdgeId ?? (selection?.kind === 'edge' ? selection.link.id : null);
  const focusEdge = focusEdgeId
    ? displayLinks.find((link) => link.id === focusEdgeId)
    : null;
  focusEdgeRef.current = focusEdge ?? null;
  const hasFocus = Boolean(focusNodeId || focusEdge);

  const isNodeActive = (node: SimNode): boolean => {
    if (aoiIds && !aoiIds.has(node.id)) return false;
    if (searchMatch && !searchMatch.nodeIds.has(node.id)) return false;
    if (!hasFocus) return true;
    if (focusNodeId) {
      return (
        node.id === focusNodeId ||
        Boolean(adjacency.get(focusNodeId)?.has(node.id))
      );
    }
    if (focusEdge) {
      const { source, target } = linkEndpoints(focusEdge);
      return node.id === source.id || node.id === target.id;
    }
    return true;
  };

  const isLinkActive = (link: SimLink): boolean => {
    if (aoiIds) {
      const [sourceId, targetId] = linkEndpointIds(link);
      if (!aoiIds.has(sourceId) || !aoiIds.has(targetId)) return false;
    }
    if (searchMatch) {
      const [sourceId, targetId] = linkEndpointIds(link);
      const matched =
        searchMatch.edgeIds.has(link.id) ||
        (searchMatch.nodeIds.has(sourceId) &&
          searchMatch.nodeIds.has(targetId));
      if (!matched) return false;
    }
    if (!hasFocus) return true;
    if (focusEdge) return link.id === focusEdge.id;
    const { source, target } = linkEndpoints(link);
    return source.id === focusNodeId || target.id === focusNodeId;
  };

  const isLabelVisible = (node: SimNode): boolean => {
    if (aoiIds) return isNodeActive(node);
    if (searchMatch) return isNodeActive(node);
    if (showAllLabels || displayNodes.length <= 30) return true;
    if (hasFocus) return isNodeActive(node);
    return alwaysLabeledIds.has(node.id);
  };

  const moveCursor = useCallback(
    (key: string) => {
      const directions: Record<string, [number, number]> = {
        ArrowUp: [0, -1],
        ArrowDown: [0, 1],
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
      };
      const dir = directions[key];
      const baseId =
        hoveredNodeId ??
        (selection?.kind === 'node' ? selection.node.id : null);
      const base = baseId ? nodesById.get(baseId) : null;
      if (!base) {
        const top = [...displayNodes]
          .filter(nodeInTime)
          .sort((a, b) => b.degree - a.degree)[0];
        if (top) setHoveredNodeId(top.id);
        return;
      }
      const neighbors = [...(adjacency.get(base.id) ?? [])]
        .map((id) => nodesById.get(id))
        .filter((n): n is SimNode => Boolean(n && nodeInTime(n)));
      let best: SimNode | null = null;
      let bestScore = 0.15;
      neighbors.forEach((neighbor) => {
        const dx = (neighbor.x ?? 0) - (base.x ?? 0);
        const dy = (neighbor.y ?? 0) - (base.y ?? 0);
        const length = Math.hypot(dx, dy) || 1;
        const score = (dx * dir[0] + dy * dir[1]) / length;
        if (score > bestScore) {
          best = neighbor;
          bestScore = score;
        }
      });
      if (best) setHoveredNodeId((best as SimNode).id);
    },
    [hoveredNodeId, selection, nodesById, displayNodes, adjacency, nodeInTime],
  );

  keyStateRef.current = {
    selection,
    aoiIds,
    focusRootId,
    hoveredNodeId,
    nodesById,
    moveCursor,
    exitAoi,
    exitFocus,
  };

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      // While the remove-confirm dialog is open, stay inert and let Radix's
      // own (document-level) listener dismiss just that top layer.
      if (confirmOpenRef.current) return;
      const {
        selection,
        aoiIds,
        focusRootId,
        hoveredNodeId,
        nodesById,
        moveCursor,
        exitAoi,
        exitFocus,
      } = keyStateRef.current;
      const target = event.target as HTMLElement;
      const inField =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;
      if (event.key === '/' && !inField) {
        event.preventDefault();
        searchWrapRef.current?.querySelector('input')?.focus();
        return;
      }
      if (event.key === 'Escape') {
        if (inField) {
          event.stopPropagation();
          target.blur();
          return;
        }
        if (selection) {
          event.preventDefault();
          event.stopPropagation();
          setSelection(null);
          return;
        }
        if (aoiIds) {
          event.preventDefault();
          event.stopPropagation();
          exitAoi();
          return;
        }
        if (focusRootId) {
          event.preventDefault();
          event.stopPropagation();
          exitFocus();
          return;
        }
        return;
      }
      if (inField || target.tagName === 'BUTTON') return;
      if (
        event.key in { ArrowUp: 1, ArrowDown: 1, ArrowLeft: 1, ArrowRight: 1 }
      ) {
        event.preventDefault();
        moveCursor(event.key);
        return;
      }
      if (event.key === 'Enter' && hoveredNodeId) {
        const node = nodesById.get(hoveredNodeId);
        if (node) setSelection({ kind: 'node', node });
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, []);

  const buildShareState = useCallback((): ViewerViewState => {
    const { x, y, k } = viewRef.current;
    return {
      view: { x: Math.round(x), y: Math.round(y), k: Number(k.toFixed(3)) },
      sel: selection?.kind === 'node' ? selection.node.id : undefined,
      q: searchQuery || undefined,
      palette: paletteId,
      clusters: showClusters || undefined,
      t: timeT ?? undefined,
      focus: focusRootId ?? undefined,
    };
  }, [selection, searchQuery, paletteId, showClusters, timeT, focusRootId]);

  shareStateRef.current = buildShareState;

  useEffect(() => {
    scheduleShareSync();
  }, [buildShareState, scheduleShareSync]);

  useEffect(() => () => clearTimeout(shareTimerRef.current), []);

  const handleCopyLink = async () => {
    const url = new URL(window.location.href);
    url.searchParams.set(
      VIEW_STATE_PARAM,
      encodeViewState(shareStateRef.current()),
    );
    window.history.replaceState(null, '', url.toString());
    try {
      await navigator.clipboard.writeText(url.toString());
      notification.success('Link copied');
    } catch (error) {
      Sentry.captureException(error, { extra: { graphName } });
      notification.error('Failed to copy link');
    }
  };

  const handleExport = async (kind: 'png' | 'svg' | 'copy') => {
    const svg = svgRef.current;
    if (!svg) return;
    try {
      if (kind === 'png') await exportGraphPng(svg, graphName);
      if (kind === 'svg') exportGraphSvg(svg, graphName);
      if (kind === 'copy') {
        await copyGraphPng(svg);
        notification.success('Image copied to clipboard');
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { graphName, kind } });
      notification.error('Failed to export graph image');
    }
  };

  const searchMatchCount = searchMatch?.nodeIds.size ?? 0;
  const focusRootLabel = focusRootId
    ? (simNodes.find((node) => node.id === focusRootId)?.label ?? '')
    : '';
  const minimapEligible = displayNodes.length > MINIMAP_THRESHOLD;
  const timelineValue = timeT ?? timeRange?.max ?? 0;

  return (
    <div
      ref={containerRef}
      className={cn(
        'bg-muted-background/40 relative h-full w-full overflow-hidden rounded-lg border',
        className,
      )}
    >
      <svg
        ref={svgRef}
        className='h-full w-full cursor-grab touch-none select-none active:cursor-grabbing'
        role='img'
        aria-label='Context graph visualization'
        onWheel={handleWheel}
        onPointerDown={handleBackgroundPointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        <g
          ref={sceneRef}
          className={cn(
            'transition-opacity duration-500',
            layoutReady ? 'opacity-100' : 'opacity-0',
          )}
        >
          {clusters ? (
            <g>
              {clusters.map((cluster) => (
                <g
                  key={cluster.id}
                  ref={(element) => {
                    if (element) {
                      hullElementsRef.current.set(cluster.id, element);
                    } else {
                      hullElementsRef.current.delete(cluster.id);
                    }
                  }}
                  className='pointer-events-none'
                >
                  <path
                    fill={cluster.color}
                    fillOpacity={0.06}
                    stroke={cluster.color}
                    strokeOpacity={0.1}
                    strokeWidth={44}
                    strokeLinejoin='round'
                    strokeLinecap='round'
                  />
                  <text
                    fontSize={11}
                    textAnchor='middle'
                    fill={cluster.color}
                    fillOpacity={0.75}
                    stroke='var(--background)'
                    strokeWidth={3}
                    strokeOpacity={0.5}
                    paintOrder='stroke'
                  >
                    {cluster.label}
                  </text>
                </g>
              ))}
            </g>
          ) : null}
          <g>
            {renderedLinks.map((link) => {
              const active = isLinkActive(link);
              return (
                <GraphLink
                  key={link.id}
                  link={link}
                  visible={linkInTime(link)}
                  active={active}
                  highlighted={active && hasFocus}
                  onHover={setHoveredEdgeId}
                  onSelect={selectLink}
                  registerElement={registerLinkElement}
                />
              );
            })}
          </g>
          <g>
            {displayNodes.map((node) => (
              <GraphNode
                key={node.id}
                node={node}
                label={node.label}
                radius={node.radius}
                color={typeColors.get(node.type) ?? 'var(--chart-1)'}
                visible={nodeInTime(node)}
                active={isNodeActive(node)}
                isFocusNode={node.id === focusNodeId}
                labelVisible={isLabelVisible(node)}
                onHover={setHoveredNodeId}
                onPointerDown={handleNodePointerDown}
                onEnterFocus={enterFocus}
                registerElement={registerNodeElement}
              />
            ))}
          </g>
          {focusEdge ? (
            <text
              ref={focusEdgeLabelRef}
              x={linkMidpoint(focusEdge).x}
              y={linkMidpoint(focusEdge).y - 6}
              fontSize={10}
              textAnchor='middle'
              fill='var(--muted-foreground)'
              stroke='var(--background)'
              strokeWidth={3}
              strokeOpacity={0.7}
              paintOrder='stroke'
              className='pointer-events-none'
            >
              {(focusEdge.pairCount ?? 0) > MAX_PARALLEL_ARCS
                ? `${truncateLabel(focusEdge.label, 32)} · 1 of ${focusEdge.pairCount} facts`
                : truncateLabel(focusEdge.label, 32)}
            </text>
          ) : null}
        </g>
      </svg>

      <div className='absolute top-3 left-3 flex items-center gap-2'>
        <div ref={searchWrapRef} className='w-56'>
          <GraphSearchInput
            initialValue={initialViewState?.q ?? ''}
            onQueryChange={setSearchQuery}
          />
        </div>
        {searchMatch ? (
          <span
            className={cn(
              'bg-background/85 rounded-md border px-2 py-1.5 text-xs backdrop-blur-sm',
              searchMatchCount === 0
                ? 'text-muted-foreground'
                : 'text-foreground',
            )}
          >
            {searchMatchCount === 0
              ? 'No matches'
              : `${searchMatchCount} ${searchMatchCount === 1 ? 'match' : 'matches'}`}
          </span>
        ) : null}
      </div>

      <div className='pointer-events-none absolute top-3 left-1/2 flex -translate-x-1/2 flex-col items-center gap-1.5'>
        {focusRootId && focusIds ? (
          <div className='bg-background/85 pointer-events-auto flex items-center gap-2 rounded-md border px-3 py-1.5 backdrop-blur-sm'>
            <span className='text-xs'>
              <span className='text-muted-foreground'>Focused on</span>{' '}
              <span className='font-medium'>
                {truncateLabel(focusRootLabel, 30)}
              </span>{' '}
              <span className='text-muted-foreground'>
                · {displayNodes.length} of {simNodes.length}
              </span>
            </span>
            <button
              type='button'
              onClick={exitFocus}
              className='text-muted-foreground hover:text-foreground'
              aria-label='Back to full graph'
            >
              <X className='size-3.5' />
            </button>
          </div>
        ) : null}
        {aoiIds ? (
          <div className='bg-background/85 pointer-events-auto flex items-center gap-2 rounded-md border px-3 py-1.5 backdrop-blur-sm'>
            <span className='text-xs'>
              <span className='font-medium'>Area selection</span>{' '}
              <span className='text-muted-foreground'>
                · {aoiIds.size} {aoiIds.size === 1 ? 'entity' : 'entities'}
              </span>
            </span>
            <button
              type='button'
              onClick={exitAoi}
              className='text-muted-foreground hover:text-foreground'
              aria-label='Clear area selection'
            >
              <X className='size-3.5' />
            </button>
          </div>
        ) : null}
      </div>

      <div
        ref={marqueeDivRef}
        className='border-primary/60 bg-primary/10 pointer-events-none absolute rounded-sm border'
        style={{ display: 'none' }}
      />

      {aoiEmptyHint ? (
        <div className='bg-background/85 text-muted-foreground absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-md border px-3 py-1.5 text-xs backdrop-blur-sm'>
          No entities in selection
        </div>
      ) : null}

      {typeCounts.length > 0 ? (
        <div className='bg-background/85 absolute bottom-3 left-3 flex max-w-[35%] flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 backdrop-blur-sm'>
          {typeCounts.map(([type, count]) => (
            <div key={type} className='flex items-center gap-1.5'>
              <span
                className='size-2.5 shrink-0 rounded-full'
                style={{ backgroundColor: typeColors.get(type) }}
              />
              <span className='text-muted-foreground text-xs'>
                {type}
                <span className='text-muted-foreground/60 ml-1'>{count}</span>
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {timeRange ? (
        <div className='absolute bottom-3 left-1/2 -translate-x-1/2'>
          <TimelineScrubber
            min={timeRange.min}
            max={timeRange.max}
            value={timelineValue}
            playing={playing}
            onChange={(value) => {
              setPlaying(false);
              setTimeT(value >= timeRange.max ? null : value);
            }}
            onTogglePlay={handleTogglePlay}
          />
        </div>
      ) : null}

      {minimapEligible && minimapOpen ? (
        <div className='bg-background/85 absolute right-14 bottom-3 overflow-hidden rounded-md border backdrop-blur-sm'>
          <svg
            ref={minimapSvgRef}
            width={MINIMAP_W}
            height={MINIMAP_H}
            className='block cursor-pointer'
            onPointerDown={(event) => {
              event.stopPropagation();
              minimapSvgRef.current?.setPointerCapture(event.pointerId);
              minimapDraggingRef.current = true;
              minimapNavigate(event);
            }}
            onPointerMove={(event) => {
              if (minimapDraggingRef.current) minimapNavigate(event);
            }}
            onPointerUp={() => {
              minimapDraggingRef.current = false;
            }}
          >
            {displayNodes.map((node) => (
              <circle
                key={node.id}
                ref={(element) => {
                  if (element) {
                    minimapDotsRef.current.set(node.id, element);
                  } else {
                    minimapDotsRef.current.delete(node.id);
                  }
                }}
                r={1.7}
                fill={typeColors.get(node.type)}
              />
            ))}
            <rect
              ref={minimapRectRef}
              fill='var(--primary)'
              fillOpacity={0.08}
              stroke='var(--primary)'
              strokeOpacity={0.7}
              strokeWidth={1.2}
              rx={2}
            />
          </svg>
        </div>
      ) : null}

      <div className='bg-background/85 absolute right-3 bottom-3 flex flex-col rounded-md border backdrop-blur-sm'>
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-8 w-8'
                  aria-label='Color palette'
                >
                  <Palette className='size-4' />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side='left'>Color palette</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align='end' side='left'>
            <DropdownMenuRadioGroup
              value={paletteId}
              onValueChange={handlePaletteChange}
            >
              {GRAPH_PALETTES.map((palette) => (
                <DropdownMenuRadioItem key={palette.id} value={palette.id}>
                  <span className='flex items-center gap-2'>
                    <span className='flex items-center gap-0.5'>
                      {palette.colors.slice(0, 5).map((color) => (
                        <span
                          key={color}
                          className='size-2.5 rounded-full'
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </span>
                    {palette.name}
                  </span>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className={cn('h-8 w-8', showClusters && 'text-primary')}
              onClick={handleToggleClusters}
              aria-label='Toggle clusters'
              aria-pressed={showClusters}
            >
              <Boxes className='size-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='left'>Toggle clusters</TooltipContent>
        </Tooltip>
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-8 w-8'
                  aria-label='Export image'
                >
                  <Download className='size-4' />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side='left'>Export image</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align='end' side='left'>
            <DropdownMenuItem onClick={() => handleExport('png')}>
              Download PNG
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('svg')}>
              Download SVG
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('copy')}>
              Copy image
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              onClick={handleCopyLink}
              aria-label='Copy link to this view'
            >
              <Link2 className='size-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='left'>Copy link to this view</TooltipContent>
        </Tooltip>
        {minimapEligible ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='ghost'
                size='icon'
                className={cn('h-8 w-8', minimapOpen && 'text-primary')}
                onClick={() => setMinimapOpen((prev) => !prev)}
                aria-label='Toggle minimap'
                aria-pressed={minimapOpen}
              >
                <MapIcon className='size-4' />
              </Button>
            </TooltipTrigger>
            <TooltipContent side='left'>Toggle minimap</TooltipContent>
          </Tooltip>
        ) : null}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              aria-label='Keyboard shortcuts'
            >
              <Keyboard className='size-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='left'>
            <div className='flex flex-col gap-1'>
              {SHORTCUTS.map(([key, action]) => (
                <div key={key} className='flex items-center gap-2'>
                  <span className='w-20 font-mono text-[10px] opacity-70'>
                    {key}
                  </span>
                  <span className='text-xs'>{action}</span>
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
        <Button
          variant='ghost'
          size='icon'
          className='h-8 w-8'
          onClick={() => zoomBy(1.35)}
          aria-label='Zoom in'
        >
          <Plus className='size-4' />
        </Button>
        <Button
          variant='ghost'
          size='icon'
          className='h-8 w-8'
          onClick={() => zoomBy(1 / 1.35)}
          aria-label='Zoom out'
        >
          <Minus className='size-4' />
        </Button>
        <Button
          variant='ghost'
          size='icon'
          className='h-8 w-8'
          onClick={fitToView}
          aria-label='Fit graph to view'
        >
          <Maximize2 className='size-4' />
        </Button>
      </div>

      {selection ? (
        <DetailCard
          key={
            selection.kind === 'node' ? selection.node.id : selection.link.id
          }
          selection={selection}
          typeColors={typeColors}
          graphId={graphId}
          canEdit={canEdit}
          onClose={() => setSelection(null)}
          onConfirmOpenChange={(open) => {
            confirmOpenRef.current = open;
          }}
          onRemoved={(removed) => {
            setSelection(null);
            setHoveredNodeId(null);
            setHoveredEdgeId(null);
            onGraphMutated?.(removed);
          }}
        />
      ) : null}
    </div>
  );
};
